import { faCircleExclamation } from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/videa-components';
import classNames from 'classnames';

import type { PerioChartIncompleteReasons } from '@/common-components/composed/PerioChart/types';

interface Props {
  isDarkMode: boolean;
  incompleteReasons?: PerioChartIncompleteReasons;
  isInsufficientBleedingForSrp: boolean;
}

export const IncompletePerioChartPill = ({
  isDarkMode,
  incompleteReasons,
  isInsufficientBleedingForSrp,
}: Props) => {
  const { t } = useLingui();

  const activeIncompleteReasons = Object.entries(incompleteReasons ?? {})
    .filter(([, value]) => value)
    .map(([key]) => key as keyof PerioChartIncompleteReasons);

  const getIncompleteReasonLabel = (key: keyof PerioChartIncompleteReasons) => {
    switch (key) {
      case 'bleedingNotRecorded':
        return isInsufficientBleedingForSrp
          ? t`Bleeding not recorded (SRP was disqualified by lack of bleeding)`
          : t`Bleeding not recorded`;
      case 'gingivalMarginNotRecorded':
        return t`Gingival margin not recorded`;
      case 'clinicalAttachmentLevelAlwaysMatchesProbingDepth':
        return t`Clinical attachment level always matches probing depth`;
      default:
        return null;
    }
  };

  return (
    <Tooltip delayDuration={300}>
      <TooltipTrigger className="vai-flex vai-items-center vai-justify-center">
        <div
          className={classNames(
            'vai-border vai-rounded-full vai-py-[2px] vai-px-3 vai-inline-flex vai-justify-center vai-items-center vai-gap-2',
            {
              'vai-border-amber-500/50 vai-bg-amber-500/20 vai-text-amber-400':
                isDarkMode,
              'vai-border-amber-200 vai-bg-amber-100 vai-text-amber-700':
                !isDarkMode,
            },
          )}
        >
          <p className="vai-font-inter vai-text-xs vai-font-semibold">
            <Trans>Incomplete perio chart</Trans>
          </p>
          <FontAwesomeIcon icon={faCircleExclamation} size="xs" />
        </div>
      </TooltipTrigger>
      <TooltipContent
        variant={isDarkMode ? 'dark' : 'default'}
        side="bottom"
        className={classNames({
          '!vai-border-amber-600/50 vai-bg-amber-950/50': isDarkMode,
          '!vai-border-amber-200 vai-bg-amber-50': !isDarkMode,
        })}
      >
        <div
          className={classNames('vai-text-[12px]', {
            'vai-text-amber-200': isDarkMode,
            'vai-text-amber-900': !isDarkMode,
          })}
        >
          <p className="vai-font-semibold vai-mb-1">
            <Trans>This perio chart is incomplete</Trans>
          </p>
          {!!activeIncompleteReasons.length && (
            <ul className="vai-list-disc vai-list-outside vai-pl-4 vai-space-y-0.5">
              {activeIncompleteReasons.map((reason) => {
                const label = getIncompleteReasonLabel(reason);
                return label ? <li key={reason}>{label}</li> : null;
              })}
            </ul>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};
